import React, { useState, useEffect } from 'react';
import type { PipelineState, QueueDiagnostics } from './types';
import { getUnifiedStatus } from './dashboardHelpers';
import { StatusBadge } from './common';
import { Tooltip } from '../Tooltip';

// Helper Utils (Inline for simplicity or imported)
const formatTimeAgo = (isoString?: string) => {
    if (!isoString) return '—';
    const ms = Date.now() - new Date(isoString).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);

    if (hr > 24) return `${Math.floor(hr / 24)}d ago`;
    if (hr > 0) return `${hr}h ago`;
    if (min > 0) return `${min}m ago`;
    return `${sec}s ago`;
};

interface DashboardProps {
    data: PipelineState[];
    activeJobs?: any[];
    diagnostics: QueueDiagnostics | null;
    meta?: { brokenCount: number; brokenIds: string[]; deployTag?: string };
    dlqEvents?: any[];
    auditLoading?: boolean;
    auditFailed?: boolean;
    onRun60s: () => Promise<any>;
    onSelectTitle: (id: string) => void;
    onReconcile: (id: string) => Promise<any>;
    kickJob: (jobId: string) => Promise<any>;
    onRepairCook: (id: string, params: any) => Promise<any>;
    requeueDlq: (eventId: string, mode: any) => Promise<any>;
    rebuildThumbs: (id: string) => Promise<any>;
    getJobStatus: (id: string) => Promise<any>;
    cancelJob: (jobId: string) => Promise<any>;
    fetchManifest?: (id: string) => Promise<number | null>;
    updateManifest?: (id: string, updates: any) => Promise<any>;
    inspectBook: (id: string, deep?: boolean) => Promise<any>;
}

export const FactoryDashboard = ({
    data, diagnostics, meta, dlqEvents = [], activeJobs = [], auditLoading = false, auditFailed = false, onRun60s, onReconcile, kickJob, cancelJob, onRepairCook, requeueDlq, rebuildThumbs, getJobStatus, inspectBook, onSelectTitle, fetchManifest, updateManifest
}: DashboardProps) => {

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [deepStatus, setDeepStatus] = useState<any>(null); // { [id]: { phase, error, etc } }
    const [edits, setEdits] = useState<Record<string, { sourceUrl?: string; voice?: string; startOffset?: number; endOffset?: number; trimStartSeconds?: number }>>({});

    // P2.5 Filters
    const [filterAttention, setFilterAttention] = useState(true); // Default ON

    // FILTER LOGIC
    const filteredData = data.filter(book => {
        if (!filterAttention) return true;
        const s = getUnifiedStatus(book);
        if (s.label === 'COMPLETED') return false;
        return true;
    });

    // EFFECT: Load deep status AND Manifest when selection changes
    useEffect(() => {
        if (!selectedId) {
            setDeepStatus(null);
            return;
        }

        let mounted = true;

        const load = async () => {
            try {
                const book = data.find(d => d.id === selectedId);
                const jobId = book?.activeJob?.id;

                // 1. Job Status
                if (jobId) {
                    setDeepStatus({ loading: true });
                    const s = await getJobStatus(jobId);
                    if (mounted) setDeepStatus(s);
                } else {
                    if (mounted) setDeepStatus(null);
                }

                // 2. Fetch Manifest (Offset) - Lazy Load
                if (fetchManifest) {
                    fetchManifest(selectedId).then(val => {
                        if (mounted && val !== null) {
                            // Update local edit state base if needed?
                            // Actually fetchManifest updates 'data' so the UI will re-render with the known offset.
                        }
                    });
                }

                // 3. Deep Inspect the book to fetch R2 derived state (shallow on initial load)
                if (inspectBook) {
                    inspectBook(selectedId, false).catch(console.error);
                }

            } catch (e) {
                if (mounted) setDeepStatus({ error: String(e) });
            }
        };
        load();

        return () => { mounted = false; };
    }, [selectedId, data, getJobStatus, fetchManifest, inspectBook]);

    // HELPERS
    const selectedBook = data.find(d => d.id === selectedId);

    const updateEdit = (id: string, field: string, value: any) => {
        setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };

    const saveEdit = async (id: string) => {
        const payload = edits[id] || {};
        const existingVoice = deepStatus?.job?.params?.voice;
        let voiceToSend = payload.voice || existingVoice || 'onyx';

        // FORCE VALIDATION: Eliminate "Frederick Douglass" bug
        const VALID_VOICES = ['onyx', 'alloy', 'echo', 'shimmer', 'fable', 'nova'];
        if (!VALID_VOICES.includes(voiceToSend)) {
            console.warn(`Invalid voice "${voiceToSend}" detected, falling back to onyx`);
            voiceToSend = 'onyx';
        }

        const params: any = { voice: voiceToSend };
        if (payload.sourceUrl) {
            params.sourceUrl = payload.sourceUrl;
            params.resolution = 'reset';
        }

        await onRepairCook(id, params);
        setEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const saveOffset = async (id: string) => {
        const payload = edits[id] || {};
        if (payload.trimStartSeconds === undefined && payload.trimStartSeconds === null) return;

        const val = Math.max(0, Math.min(3600, Number(payload.trimStartSeconds) || 0));

        if (updateManifest) {
            try {
                await updateManifest(id, { trimStartSeconds: val });
                // Clear edit state for this field? Or keep it?
                // Keep it or clear? Defaults to manifest value.
                setEdits(prev => ({ ...prev, [id]: { ...(prev[id]), trimStartSeconds: undefined } }));
            } catch (e) { alert("Failed to save offset: " + e); }
        } else {
            alert("Update Manifest not supported in this version");
        }
    };

    // DEBUG: Scroll Telemetry
    const listRef = React.useRef<HTMLDivElement>(null);
    const [scrollDebug, setScrollDebug] = React.useState('init');

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        const handler = () => {
            setScrollDebug(`${Math.round(el.scrollTop)} / ${el.scrollHeight} / ${el.clientHeight}`);
        };
        el.addEventListener('scroll', handler);
        // Initial and periodic check
        handler();
        const intervals = [100, 500, 1000].map(ms => setInterval(handler, ms));
        return () => {
            el.removeEventListener('scroll', handler);
            intervals.forEach(clearInterval);
        };
    }, [data]);

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', color: '#e0e0e0', overflow: 'hidden' }}>

            {/* 1. HEADER */}
            <div style={{
                flexShrink: 0,
                padding: '10px 20px',
                background: '#111',
                borderBottom: '1px solid #333',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace' }}>
                        UI v0.9.268-patch
                        {auditLoading && <span style={{ color: '#29b6f6', marginLeft: 8 }}>↻ Syncing...</span>}
                        {auditFailed && <span style={{ color: '#ef5350', marginLeft: 8 }}>⚠️ Audit Offline</span>}
                    </div>

                    {/* FILTERS */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#1a1a1a', padding: '2px 8px', borderRadius: 4, border: '1px solid #333' }}>
                        <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', color: filterAttention ? '#fff' : '#888', fontWeight: filterAttention ? 'bold' : 'normal' }}>
                            <input type="checkbox" checked={filterAttention} onChange={e => setFilterAttention(e.target.checked)} />
                            Needs Attention
                        </label>
                    </div>

                    {/* DEBUG READOUT */}
                    <div style={{ fontSize: '9px', color: '#444', fontFamily: 'monospace', border: '1px solid #222', padding: '2px 4px' }}>
                        SCROLL: {scrollDebug}
                    </div>
                </div>

                <div style={{ fontSize: '11px', display: 'flex', gap: '15px' }}>
                    <span style={{ color: diagnostics?.consumerAlive ? '#4caf50' : '#ef5350', fontWeight: 'bold' }}>
                        {diagnostics?.consumerAlive ? 'CONSUMER ONLINE' : 'CONSUMER OFFLINE'}
                    </span>
                    <span style={{ color: '#888' }}>Last Beat: {diagnostics?.lastBeatAgeSeconds ?? '?'}s ago</span>
                </div>
            </div>

            {/* 2. SPLIT VIEW CONTAINER */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

                {/* LEFT: LIST (Scrollable) */}
                <div ref={listRef} style={{
                    flex: '1',
                    overflowY: 'auto',
                    borderRight: '1px solid #333',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#1e1e1e', zIndex: 10, boxShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                            <tr style={{ color: '#888', textAlign: 'left' }}>
                                <th style={{ padding: '8px 12px' }}>BOOK ID</th>
                                <th style={{ padding: '8px' }}>STATUS</th>
                                <th style={{ padding: '8px', textAlign: 'center' }} title="Cover (Click to Inspect)">IMG</th>
                                <th style={{ padding: '8px', textAlign: 'center' }} title="Offset (skip intro)">OFFSET</th>
                                <th style={{ padding: '8px', textAlign: 'center' }} title="Text/Reconcile">TXT</th>
                                <th style={{ padding: '8px', textAlign: 'center' }} title="Audio (Click to Play)">AUD</th>
                                <th style={{ padding: '8px' }}>PROGRESS</th>
                                <th style={{ padding: '8px' }}>UPDATED</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.slice().sort((a, b) => {
                                // Sort by UpdatedAt Desc
                                const ta = new Date(a.updatedAt || a.activeJob?.updatedAt || 0).getTime();
                                const tb = new Date(b.updatedAt || b.activeJob?.updatedAt || 0).getTime();
                                return tb - ta;
                            }).map(book => {
                                const isSel = selectedId === book.id;
                                const st = getUnifiedStatus(book);
                                const job = book.activeJob;
                                const progressPct = Math.floor((job?.progress || 0) * 100);

                                const opacityIcon = (active: boolean, icon: string) => <span style={{ opacity: active ? 1 : 0.2, fontSize: '14px' }}>{icon}</span>;

                                return (
                                    <tr
                                        key={book.id}
                                        onClick={() => setSelectedId(book.id)}
                                        style={{
                                            background: isSel ? '#0d47a1' : 'transparent',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #222'
                                        }}
                                        className="hover-row"
                                    >
                                        <td style={{ padding: '8px 12px', maxWidth: '300px' }}>
                                            <div style={{ fontWeight: '500', color: isSel ? '#fff' : '#ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {book.title || book.id}
                                            </div>
                                            <div style={{ fontSize: '10px', color: isSel ? '#bbb' : '#666', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>{book.id}</span>
                                                {book.sourceUrl && (
                                                    <a href={book.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: isSel ? '#90caf9' : '#1e88e5', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>
                                                        [src]
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <Tooltip content={book.derivedStatus?.reason || st.label}>
                                                <span style={{
                                                    fontSize: '10px', fontWeight: 'bold',
                                                    background: st.color, color: '#fff', // White text for better contrast on dark badges
                                                    padding: '2px 6px', borderRadius: '3px',
                                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                }}>
                                                    {st.label}
                                                </span>
                                            </Tooltip>
                                        </td>
                                        {/* THUMBNAIL */}
                                        <td style={{ padding: '4px', textAlign: 'center' }}
                                            onClick={(e) => { e.stopPropagation(); setSelectedId(book.id); }}>
                                            <div style={{ position: 'relative', width: 30, height: 30, background: '#333', overflow: 'hidden', borderRadius: 2, margin: '0 auto' }}>
                                                <img
                                                    src={`/api/library/cover/${book.id}/cover_grid.webp`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                        (e.target as HTMLImageElement).parentElement!.innerText = book.health?.hasCover ? '🖼️' : '❌';
                                                    }}
                                                />
                                                <button
                                                    title="Regenerate Thumbs"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (!confirm(`Regenerate thumbnails for ${book.id}?`)) return;
                                                        await rebuildThumbs(book.id);
                                                    }}
                                                    style={{
                                                        position: 'absolute', bottom: 0, right: 0,
                                                        width: 12, height: 12, padding: 0, fontSize: 8,
                                                        border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                    }}>
                                                    ↻
                                                </button>
                                                <div
                                                    title="Open Full Cover"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(`/api/library/cover/${book.id}/cover_hi.webp`, '_blank');
                                                    }}
                                                    style={{ position: 'absolute', inset: 0, bottom: 12, cursor: 'zoom-in' }}
                                                />
                                            </div>
                                        </td>
                                        {/* OFFSET */}
                                        <td style={{ padding: '8px', textAlign: 'center', color: '#aaa', fontSize: '11px' }}>
                                            {book.trimStartSeconds !== undefined ? `${book.trimStartSeconds}s` : '—'}
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                            <Tooltip content={book.derivedStatus?.sourceCount ? `${book.derivedStatus.sourceCount} text fragments` : "No Text Source"}>{opacityIcon((book.derivedStatus?.sourceCount || 0) > 0, '📄')}</Tooltip>
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}
                                            onClick={(e) => { e.stopPropagation(); setSelectedId(book.id); }}>
                                            <Tooltip content={`Audio: ${book.derivedStatus?.audioCount || 0} / ${book.derivedStatus?.totalChunks || '?'}`}>
                                                {opacityIcon((book.derivedStatus?.audioCount || 0) > 0, '🎵')}
                                            </Tooltip>
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            {book.derivedStatus && (st.label === 'PROCESSING' || st.label === 'NEEDS REPAIR' || st.label === 'PARTIAL AUDIO' || st.label === 'STALLED') ? (() => {
                                                const pct = Math.floor((book.derivedStatus.progress || 0) * 100);
                                                return (
                                                    <div style={{ width: '100px' }}>
                                                        <div style={{ fontSize: '10px', color: isSel ? '#ccc' : '#aaa', display: 'flex', justifyContent: 'space-between' }}>
                                                            <span>{pct}%</span>
                                                            <span>{book.derivedStatus.chunksCompleted || 0}/{book.derivedStatus.totalChunks || '?'}</span>
                                                        </div>
                                                        <div style={{ height: '3px', background: '#444', marginTop: '2px', borderRadius: '2px' }}>
                                                            <div style={{ height: '100%', width: `${pct}%`, background: st.label === 'STALLED' ? '#ffb74d' : (st.label === 'NEEDS REPAIR' ? '#ef5350' : '#2196f3') }}></div>
                                                        </div>
                                                    </div>
                                                );
                                            })() : <span style={{ color: '#444' }}>-</span>}
                                        </td>
                                        <td style={{ padding: '8px', color: isSel ? '#ccc' : '#888', whiteSpace: 'nowrap', fontSize: '11px' }}>
                                            {formatTimeAgo(book.updatedAt || job?.lastChunkAt || job?.updatedAt)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* RIGHT: DETAILS (Fixed Width or Flex) */}
                {selectedId && (
                    <div style={{
                        width: '400px',
                        background: '#181818',
                        borderLeft: '1px solid #333',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        flexShrink: 0
                    }}>
                        {/* DETAILS HEADER */}
                        <div style={{ padding: '15px', borderBottom: '1px solid #333', background: '#1e1e1e' }}>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
                                {selectedBook?.title || selectedId}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {selectedId}
                            </div>
                            <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => onReconcile(selectedId)}
                                    style={{ flex: 1, padding: '6px', background: '#333', border: '1px solid #555', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                    Rescan Book
                                </button>
                                <button
                                    onClick={() => inspectBook(selectedId, true)}
                                    title="Forces a deep scan of R2 for missing chunks and exact state"
                                    style={{ flex: 1, padding: '6px', background: '#333', border: '1px solid #555', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                    Refresh Details
                                </button>
                                <button
                                    onClick={() => setSelectedId(null)}
                                    style={{ padding: '6px 12px', background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' }}>
                                    ✕
                                </button>
                            </div>
                            <div style={{ marginTop: '10px', padding: '6px 10px', background: 'rgba(235, 104, 76, 0.1)', border: '1px solid rgba(235, 104, 76, 0.3)', borderRadius: '4px', fontSize: '10px', color: '#eb684c' }}>
                                ⚠️ Deep inspect scans R2, generating Class A billing operations. Use sparingly.
                            </div>
                        </div>

                        {/* DETAILS BODY */}
                        <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                            {/* ACTIVE JOB CARD */}
                            {selectedBook?.activeJob ? (
                                <div style={{ padding: '10px', background: '#222', borderRadius: '4px', border: '1px solid #333' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', marginBottom: '5px' }}>ACTIVE JOB</div>
                                    <div style={{ fontSize: '12px' }}>
                                        <div style={{ marginBottom: '5px' }}>
                                            <span style={{ color: '#aaa' }}>ID:</span> <span style={{ fontFamily: 'monospace' }}>{selectedBook.activeJob.id}</span>
                                        </div>
                                        <div style={{ marginBottom: '5px' }}>
                                            <span style={{ color: '#aaa' }}>Step:</span> {selectedBook.activeJob.step}
                                        </div>
                                        <div style={{ marginBottom: '5px' }}>
                                            <span style={{ color: '#aaa' }}>Progress:</span> {Math.floor((selectedBook.activeJob.progress || 0) * 100)}%
                                            <span style={{ color: '#666', marginLeft: '5px' }}>
                                                ({selectedBook.activeJob.chunksCompleted}/{selectedBook.activeJob.totalChunks})
                                            </span>
                                        </div>
                                        {selectedBook.activeJob.logs && selectedBook.activeJob.logs.length > 0 && (
                                            <div style={{ marginTop: '5px', color: '#ef5350', background: '#300', padding: '5px', borderRadius: '3px', fontSize: '11px' }}>
                                                {selectedBook.activeJob.logs[0]}
                                            </div>
                                        )}
                                        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                                            <button
                                                onClick={() => kickJob(selectedBook.activeJob!.id)}
                                                style={{ padding: '5px 10px', background: '#d32f2f', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>
                                                KICK / RETRY
                                            </button>
                                            <Tooltip content={selectedBook.activeJob!.id ? "Open Full Details" : "Missing Job ID"}>
                                                <button
                                                    disabled={!selectedBook.activeJob!.id}
                                                    onClick={() => onSelectTitle(selectedBook.id)}
                                                    style={{
                                                        opacity: selectedBook.activeJob!.id ? 1 : 0.5,
                                                        cursor: selectedBook.activeJob!.id ? 'pointer' : 'not-allowed',
                                                        padding: '5px 10px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '3px', fontSize: '11px'
                                                    }}>
                                                    VIEW DETAILS ➜
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* BOOK INSPECTION CARD (No Active Job) */
                                <div style={{ padding: '10px', background: '#222', borderRadius: '4px', border: '1px solid #333' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', marginBottom: '8px' }}>BOOK INSPECTION</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', color: '#ddd' }}>
                                        <div>State: <b style={{ color: getUnifiedStatus(selectedBook!).color }}>{selectedBook?.derivedStatus?.status || 'Unknown'}</b></div>
                                        <div>Reason: <span style={{ color: '#aaa', fontSize: '10px' }}>{selectedBook?.derivedStatus?.reason || '-'}</span></div>
                                        <div>Playlist: {selectedBook?.health?.hasPlaylist ? '✅' : '❌'}</div>
                                        <div>Reconcile: {selectedBook?.health?.hasReconcile ? '✅' : '❌'}</div>
                                        <div>TXT Source: {selectedBook?.derivedStatus?.sourceCount || 0} files</div>
                                        <div>Audio: {selectedBook?.derivedStatus?.audioCount || 0} / {selectedBook?.derivedStatus?.totalChunks || '?'}</div>
                                        <div>Covers: {Object.values(selectedBook?.derivedStatus?.covers || {}).filter(Boolean).length} / 4</div>
                                        {selectedBook?.derivedStatus?.missingCount && selectedBook.derivedStatus.missingCount > 0 ? (
                                            <div style={{ gridColumn: '1 / -1', color: '#ef5350' }}>
                                                Missing Count: {selectedBook.derivedStatus.missingCount}
                                                {selectedBook.derivedStatus.missingRanges && selectedBook.derivedStatus.missingRanges.length > 0 && (
                                                    <span style={{ color: '#aaa', marginLeft: '8px' }}>
                                                        Ranges: {selectedBook.derivedStatus.missingRanges.map(r => r.start === r.end ? String(r.start) : `${r.start}-${r.end}`).join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* P2.5 TRUTH DETAILS */}
                                    {selectedBook?.health?.keys && (
                                        <div style={{ marginTop: 12, padding: 6, background: '#111', borderRadius: 3, fontSize: 9, fontFamily: 'monospace', color: '#888', border: '1px solid #333' }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#aaa' }}>R2 KEY TRUTH</div>
                                            {Object.entries(selectedBook.health.keys).map(([k, v]: [string, any]) => {
                                                const exists = Array.isArray(v) ? v.some(x => x.exists) : v.exists;
                                                const label = Array.isArray(v) ? (exists ? 'FOUND (Candidate)' : 'MISSING (All)') : (exists ? 'FOUND' : 'MISSING');
                                                return (
                                                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                                        <span>{k}</span>
                                                        <span style={{ color: exists ? '#4caf50' : '#d32f2f' }}>{label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '11px' }}>
                                        {selectedBook?.health?.hasPlaylist && (
                                            <a href={`/api/library/playlist/${selectedBook.id}.json`} target="_blank" rel="noreferrer" style={{ color: '#64b5f6', textDecoration: 'none', border: '1px solid #333', padding: '2px 6px', borderRadius: '3px' }}>
                                                Playlist JSON
                                            </a>
                                        )}
                                        {(selectedBook?.health?.hasAudio || selectedBook?.health?.hasPlaylist) && (
                                            <a href={`/api/library/audio/${selectedBook.id}/0.mp3`} target="_blank" rel="noreferrer" style={{ color: '#64b5f6', textDecoration: 'none', border: '1px solid #333', padding: '2px 6px', borderRadius: '3px' }}>
                                                Play Audio
                                            </a>
                                        )}
                                        {selectedBook?.health?.hasCover && (
                                            <a href={`/api/library/cover/${selectedBook.id}/cover_512.png`} target="_blank" rel="noreferrer" style={{ color: '#64b5f6', textDecoration: 'none', border: '1px solid #333', padding: '2px 6px', borderRadius: '3px' }}>
                                                View Cover
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* REPAIR CARD (FIX / EDIT) */}
                            <div style={{ padding: '10px', background: '#222', borderRadius: '4px', border: '1px solid #333' }}>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', marginBottom: '10px' }}>FIX / EDIT</div>

                                {/* Voice */}
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '3px' }}>Voice</label>
                                    <select
                                        value={edits[selectedId]?.voice || deepStatus?.job?.params?.voice || 'onyx'}
                                        onChange={e => updateEdit(selectedId, 'voice', e.target.value)}
                                        style={{ width: '100%', background: '#333', border: '1px solid #555', color: '#fff', padding: '5px', borderRadius: '3px' }}
                                    >
                                        {['onyx', 'alloy', 'echo', 'shimmer', 'fable', 'nova'].map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                </div>

                                {/* Audio Start Offset (New) */}
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '3px' }}>Audio Start Offset (sec)</label>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="3600"
                                            placeholder={String(selectedBook?.trimStartSeconds || 0)}
                                            value={edits[selectedId]?.trimStartSeconds ?? ''}
                                            onChange={e => updateEdit(selectedId, 'trimStartSeconds', e.target.value)}
                                            style={{ flex: 1, background: '#333', border: '1px solid #555', color: '#fff', padding: '5px', borderRadius: '3px', fontSize: '11px' }}
                                        />
                                        <button
                                            onClick={() => saveOffset(selectedId)}
                                            style={{
                                                padding: '5px 10px', background: '#00695c', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold'
                                            }}
                                            title="Save Offset Only"
                                        >
                                            SAVE
                                        </button>
                                    </div>
                                    <div style={{ fontSize: '9px', color: '#666', marginTop: 2 }}>
                                        Skip intro music/preamble. Current: {selectedBook?.trimStartSeconds ?? 0}s
                                    </div>
                                </div>

                                {/* Source URL */}
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '3px' }}>Source URL</label>
                                    <input
                                        type="text"
                                        placeholder="https://gutenberg..."
                                        value={edits[selectedId]?.sourceUrl ?? ''}
                                        onChange={e => updateEdit(selectedId, 'sourceUrl', e.target.value)}
                                        style={{ width: '100%', background: '#333', border: '1px solid #555', color: '#fff', padding: '5px', borderRadius: '3px', fontSize: '11px' }}
                                    />
                                </div>

                                <button
                                    onClick={() => saveEdit(selectedId)}
                                    style={{ width: '100%', padding: '8px', background: '#2196f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                                    Apply + Fix
                                </button>
                            </div>

                            {/* DEEP STATUS */}
                            {deepStatus && (
                                <div style={{ padding: '10px', background: '#1a1a1a', borderTop: '1px solid #333' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>DEEP INSPECTION</div>
                                    <pre style={{ fontSize: '10px', color: '#888', whiteSpace: 'pre-wrap', overflow: 'hidden' }}>
                                        {JSON.stringify(deepStatus, null, 2)}
                                    </pre>
                                </div>
                            )}

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

